import {
  AppBar,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material'
import DropDownPicker, { DropDownMultiselect } from 'components/common/DropDownPicker'
import { useCallback, useEffect, useRef, useState } from 'react'

import ClearIcon from '@mui/icons-material/Clear'
import { Icon as Iconify } from '@iconify/react'
import RibbonMenu from 'components/common/Ribbon/RibbonComponents/RibbonMenu'
import Tabs from 'components/common/Tabs'
import { appColors } from 'utils'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'

function Ribbon({
  tabs = [],
  selectedEditor,
  setSelectedEditor,
  barHeight = 60,
  useCompression = false,
  tabStyle = {},
}) {
  const { getRibbonBackgroundColor, getActionBarColor } = useInnovaTheme()
  if (!tabs || tabs.length === 0) return null
  if (tabs.length > 1 && (!selectedEditor || !setSelectedEditor)) return null

  if (tabs.length === 1) {
    return <RibbonBar panels={tabs[0].panels} height={barHeight} useCompression={useCompression} />
  }

  const currentTab = tabs.find((tab) => tab.key === selectedEditor)

  return (
    <Box
      sx={{
        width: '100%',
        position: 'sticky',
        zIndex: 1,
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: getActionBarColor(),
        textOverflow: 'ellipsis',
        justifyContent: 'center',
        alignItems: 'start',
        gap: 0,
      }}>
      <Tabs
        selectedEditor={selectedEditor}
        setSelectedEditor={setSelectedEditor}
        tabs={tabs}
        style={{ selectedBackgroundColor: getRibbonBackgroundColor(), ...tabStyle }}
      />

      {currentTab && currentTab.panels && (
        <RibbonBar panels={currentTab.panels} height={barHeight} useCompression={useCompression} />
      )}
    </Box>
  )
}

export function RibbonBar({ panels = [], height = 60, useCompression = false }) {
  const { getRibbonBackgroundColor, ribbonStyle } = useInnovaTheme()

  const containerRef = useRef(null)
  const measureRef = useRef(null)
  const [shouldCompress, setShouldCompress] = useState(false)

  const renderItems = (items = [], groupKey) =>
    items.map((item, index) => (
      <Box key={`${groupKey}-${index}`} sx={{ display: 'flex', alignItems: 'center' }}>
        {item && item}
      </Box>
    ))

  const renderPanel = (panel, index, totalCount) => {
    const isLast = index === totalCount - 1
    const dividerSx = {
      borderRight: !isLast || panel.printSideDivider ? '1px solid rgba(255,255,255,0.3)' : 'none',
    }

    if (!panels || !Array.isArray(panels) || panels.length === 0) return null

    return (
      <Box
        key={panel.panelName || index}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '100%',
          flexShrink: 0,
          ...dividerSx,
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pr: !isLast ? 1 : 0,
            pl: index !== 0 ? 1 : 0,
          }}>
          {renderItems(panel.items, `panel-${index}`)}
        </Box>

        {panel.panelName && (
          <Box
            sx={{
              color: ribbonStyle.color,
              textAlign: 'center',
              fontSize: '14px',
              paddingBottom: 0.25,
              display: { xs: 'none', sm: 'none', md: 'none', lg: 'flex' },
            }}>
            {panel.panelName}
          </Box>
        )}
      </Box>
    )
  }

  const renderCompressedPanel = (panel, index, totalCount) => {
    const { compress } = panel
    const isLast = index === totalCount - 1

    if (compress?.skip) return renderPanel(panel, index, totalCount)

    const pinned = compress?.pin ?? []
    const menuSource = compress?.menu ?? panel.items ?? []
    const compressedIcon = compress?.compressedIcon

    const menuItems = menuSource.filter(Boolean).map((item, i) => ({
      type: 'custom',
      key: `${panel.panelName ?? index}-item-${i}`,
      keepOpen: true,
      content: item,
    }))

    const hasPinned = pinned.length > 0
    const hasMenu = menuItems.length > 0

    if (!hasPinned && !hasMenu) return null

    return (
      <Box
        key={panel.panelName || index}
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: 0.5,
          height: '100%',
          flexShrink: 0,
          pr: !isLast ? 1 : 0,
          pl: index !== 0 ? 1 : 0,
          borderRight: !isLast || panel.printSideDivider ? '1px solid rgba(255,255,255,0.3)' : 'none',
        }}>
        {hasPinned && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 0.5,
            }}>
            {pinned.filter(Boolean).map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center' }}>
                {item}
              </Box>
            ))}
          </Box>
        )}

        {hasMenu && (
          <RibbonMenu
            label={compress?.label ?? panel.panelName}
            icon={compressedIcon ?? <Iconify icon='ri:layout-grid-line' />}
            menuIcon={<Iconify icon='ri:arrow-down-s-line' />}
            height='100%'
            items={menuItems}
            minWidth={220}
            variant='ribbonButton'
            buttonSx={{ px: 0.75, justifyContent: 'flex-start' }}
          />
        )}
      </Box>
    )
  }

  const checkOverflow = useCallback(() => {
    if (!useCompression || !panels?.length) {
      setShouldCompress(false)
      return
    }

    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    setShouldCompress(measure.scrollWidth > container.clientWidth)
  }, [useCompression, panels])

  useEffect(() => {
    const container = containerRef.current
    const measure = measureRef.current
    if (!container || !measure) return

    const observer = new ResizeObserver(checkOverflow)
    observer.observe(container)
    observer.observe(measure)

    return () => observer.disconnect()
  }, [checkOverflow])

  const toolbarSx = {
    height,
    py: 0.5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflowX: useCompression ? 'hidden' : 'auto',
    overflowY: 'hidden',
    '&::-webkit-scrollbar': { height: '4px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '2px',
      '&:hover': { cursor: 'pointer', backgroundColor: 'rgba(255, 255, 255, 0.5)' },
    },
  }

  return (
    <>
      <AppBar
        position='static'
        color='default'
        sx={{
          backgroundColor: getRibbonBackgroundColor(),
          borderRadius: 1,
          border: '1px solid rgba(255,255,255,0.2)',
        }}>
        <Toolbar ref={containerRef} sx={toolbarSx}>
          {panels.map((panel, index) =>
            shouldCompress
              ? renderCompressedPanel(panel, index, panels.length)
              : renderPanel(panel, index, panels.length),
          )}
        </Toolbar>
      </AppBar>

      {useCompression && (
        <Box
          ref={measureRef}
          sx={{
            position: 'absolute',
            visibility: 'hidden',
            pointerEvents: 'none',
            height: height,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            display: 'flex',
            alignItems: 'center',
            px: 0,
            py: 0.5,
          }}>
          {panels.map((panel, index) => renderPanel(panel, index, panels.length))}
        </Box>
      )}
    </>
  )
}

export const FormControlRibbonButton = ({
  icon,
  iconColor,
  label,
  disabled = false,
  onClick,
  labelPlacement = 'start',
}) => {
  const { getTextColor } = useInnovaTheme()

  return (
    <FormControlLabel
      sx={{
        margin: 0,
        padding: 0,
        '& .MuiFormControlLabel-label': { padding: 0 },
      }}
      control={
        <Button
          onClick={onClick}
          disabled={disabled}
          sx={{
            color: iconColor || getTextColor(),
            minWidth: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 0,
            pt: 0.5,
            gap: 0.5,
          }}>
          {icon && <Box sx={{ display: 'flex', alignItems: 'center', padding: 0 }}>{icon}</Box>}
        </Button>
      }
      label={
        <Typography
          color={getTextColor()}
          fontSize={{ xs: '13px', xl: '14px' }}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textTransform: 'capitalize',
            lineHeight: 1,
          }}>
          {label}
        </Typography>
      }
      labelPlacement={labelPlacement}
    />
  )
}

export const RibbonNumberField = ({
  disabled = false,
  value = '',
  onChange = () => {},
  placeholder = '',
  labelFontSize,
  label,
  labelPlacement = 'start', // 'start', 'end', 'top', 'bottom', 'placeholder'
  min = -100000,
  max = 100000,
  inputId = '',
  precision = 3,
  width = '75px',
  height = '20px',
  disableErrors,
  clearable = false,
  variant = 'outlined',

  showToggle = false,
  toggleValue = true,
  onToggleChange = () => {},
  disableWhenToggleOff = true,
  toggleTooltip = 'Enable',
}) => {
  const [textValue, setTextValue] = useState(value?.toString() || '')
  const [isFocused, setIsFocused] = useState(false)
  const { ribbonStyle } = useInnovaTheme()

  const effectiveDisabled = disabled || (showToggle && disableWhenToggleOff && !toggleValue)

  useEffect(() => {
    if (value === null || value === undefined || value === '') {
      setTextValue('')
      return
    }
    setTextValue(value?.toString())
  }, [value])

  const parsedValue = parseFloat(textValue)

  const displayedValue = () => {
    if (textValue === '') return ''
    if (isNaN(parsedValue)) return ''
    if (textValue?.split('.')[1]?.length > precision) return parsedValue.toFixed(precision)
    return textValue
  }

  const isError = disableErrors
    ? false
    : textValue !== '' && (isNaN(parsedValue) || parsedValue < min || parsedValue > max)

  const commit = () => {
    if (textValue === '') {
      if (value !== null && value !== '' && value !== undefined) onChange(null)
      return
    }

    if (isError) {
      setTextValue(value?.toString() || '')
      return
    }

    if (value === parsedValue) return
    onChange(parsedValue)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commit()
  }

  const showClear = clearable && !effectiveDisabled && textValue !== ''

  const toggleAdornment = showToggle ? (
    <Tooltip title={toggleTooltip}>
      <IconButton
        size='small'
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onToggleChange(!toggleValue)}
        sx={{
          p: 0.25,
          mr: showClear ? 0.25 : 0,
          opacity: toggleValue ? 1 : 0.35,
        }}>
        <Iconify icon='mdi:eye' width={16} />
      </IconButton>
    </Tooltip>
  ) : null

  const clearAdornment = showClear ? (
    <Tooltip title='Clear'>
      <IconButton
        size='small'
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          setTextValue('')
          onChange(null)
        }}
        sx={{ p: 0.25, mr: 0.25 }}>
        <ClearIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  ) : null

  const textField = (
    <TextField
      type='tel'
      disabled={effectiveDisabled}
      placeholder={labelPlacement === 'placeholder' ? placeholder : placeholder}
      label={labelPlacement === 'placeholder' ? label : undefined}
      value={isFocused ? textValue : displayedValue()}
      onFocus={() => setIsFocused(true)}
      onBlur={() => {
        setIsFocused(false)
        commit()
      }}
      onChange={(e) => setTextValue(e.target.value)}
      onKeyDown={handleKeyDown}
      error={isError}
      variant={variant}
      inputProps={{
        min,
        max,
        id: inputId || `ribbonNumberField-${label}`,
        style: { color: ribbonStyle.color, backgroundColor: ribbonStyle.backgroundColor },
      }}
      InputProps={{
        endAdornment:
          showToggle || showClear ? (
            <InputAdornment position='end' sx={{ ml: 0.25 }}>
              {toggleAdornment}
              {clearAdornment}
            </InputAdornment>
          ) : null,
      }}
      sx={{
        width,
        margin: '2px',
        '& .MuiInputBase-root': {
          height,
          fontSize: '14px',
          backgroundColor: ribbonStyle.backgroundColor,
        },
        '& .MuiInputBase-input': {
          height: '100%',
          padding: '0 8px',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: isError ? 'red' : effectiveDisabled ? 'green' : '#c0c0c0',
          borderWidth: 1,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: isError ? 'red' : '#429CEB',
        },
        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: isError ? 'red' : '#429CEB',
          borderWidth: 1,
          boxShadow: isError ? 'none' : '0 0 4px rgba(66,156,235,0.3)',
        },
        '& .MuiInputBase-input.Mui-disabled': {
          WebkitTextFillColor: ribbonStyle.disabledColor,
        },
        ...(labelPlacement === 'placeholder' && {
          '& .MuiInputLabel-root': {
            fontSize: labelFontSize || '14px',
            color: ribbonStyle.color,
          },
        }),
      }}
    />
  )

  if (labelPlacement === 'placeholder') {
    return textField
  }

  return (
    <FormControlLabel
      control={textField}
      label={
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            fontSize: labelFontSize || { xs: '13px', xl: '14px' },
            color: effectiveDisabled ? ribbonStyle.disabledColor : ribbonStyle.color,
            mr: labelPlacement === 'start' ? 0.5 : 0,
          }}>
          {label}
        </Typography>
      }
      labelPlacement={labelPlacement}
      sx={{ margin: 0 }}
    />
  )
}

export const RibbonTextField = ({
  disabled = false,
  value = '',
  onChange = () => {},
  label,
  labelPlacement = 'start',
  labelFontSize,
  inputId = '',
  size = 'small',
  width = 'auto',
}) => {
  const [textValue, setTextValue] = useState(value)
  const { ribbonStyle } = useInnovaTheme()

  useEffect(() => {
    setTextValue(value)
  }, [value])

  const handleEnterSubmit = () => {
    if (value === textValue) return
    onChange(textValue)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleEnterSubmit()
    }
  }

  return (
    <FormControlLabel
      control={
        <TextField
          type='text'
          disabled={disabled}
          value={textValue || ''}
          onBlur={() => {
            handleEnterSubmit()
          }}
          onChange={(e) => setTextValue(e.target.value)}
          onKeyDown={handleKeyDown}
          inputProps={{
            id: inputId || `ribbonTextField-${label}`,
            style: { fontSize: '14px', color: ribbonStyle.color },
          }}
          size={size}
          sx={{
            width: width,
            backgroundColor: ribbonStyle.backgroundColor,
            margin: '2px',
            '& .MuiInputBase-root': {
              height: '20px',
              fontSize: '14px',
              backgroundColor: ribbonStyle.backgroundColor,
            },
            '& .MuiInputBase-input': {
              height: '100%',
              padding: '0 8px',
              boxSizing: 'border-box',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: disabled ? 'green' : '#c0c0c0',
              borderWidth: 1,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#429CEB',
            },
            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#429CEB',
              borderWidth: 1,
              boxShadow: '0 0 4px rgba(66,156,235,0.3)',
            },
            '& .MuiInputBase-input.Mui-disabled': {
              WebkitTextFillColor: ribbonStyle.disabledColor,
            },
          }}
        />
      }
      label={
        <Typography
          sx={{
            whiteSpace: 'nowrap',
            fontSize: labelFontSize || { xs: '13px', xl: '14px' },
            color: disabled ? ribbonStyle.disabledColor : ribbonStyle.color,
            mr: labelPlacement === 'start' ? 0.5 : 0,
          }}>
          {label}
        </Typography>
      }
      labelPlacement={labelPlacement}
      sx={{ margin: 0 }}
    />
  )
}

export const RibbonPicker = ({
  label,
  labelPlacement = 'bottom', // 'bottom' | 'end' | 'start' | 'top' | 'placeholder'
  width,
  height = undefined,
  maxWidth,
  value = '',
  onChange,
  options = [],
  disabled = false,
  labelFontSize = 13,
  toolTipText = null,
  backColor,
}) => {
  const { ribbonStyle } = useInnovaTheme()

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <FormControlLabel
        labelPlacement={labelPlacement !== 'placeholder' ? labelPlacement : 'end'}
        sx={{ margin: 0 }}
        control={
          <DropDownPicker
            disabled={disabled}
            value={value || ''}
            width={width}
            height={height}
            maxWidth={maxWidth}
            onChange={onChange}
            listOptions={options}
            fontStyle={{
              fontSize: 12,
              fontColor: ribbonStyle.color,
              backColor: backColor || ribbonStyle.backgroundColor,
            }}
            label={labelPlacement === 'placeholder' ? label : undefined}
          />
        }
        label={
          labelPlacement !== 'placeholder' ? (
            <Typography
              sx={{
                whiteSpace: 'nowrap',
                fontSize: { xs: `${labelFontSize}px`, xl: `${labelFontSize + 1}px` },
                color: disabled ? ribbonStyle.disabledColor : ribbonStyle.color,
              }}>
              {label}
            </Typography>
          ) : undefined
        }
      />
      {toolTipText ? (
        <Tooltip arrow title={toolTipText} placement={'right'}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: 0.8,
              lineHeight: 0,
              '&:hover': { opacity: 1 },
            }}>
            <Iconify icon='material-symbols:help-outline' color={appColors.dirNptColor} width={15} height={15} />
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  )
}

export const RibbonMultiSelect = ({
  label,
  labelPlacement = 'bottom', // 'bottom' | 'end' | 'start' | 'top' | 'placeholder'
  selected = [],
  onClose,
  onChange,
  options = [],
  disabled = false,
  width = '250px',
  height = undefined,
}) => {
  const { ribbonStyle } = useInnovaTheme()

  return (
    <FormControlLabel
      labelPlacement={labelPlacement !== 'placeholder' ? labelPlacement : 'end'}
      sx={{ margin: 0 }}
      control={
        <DropDownMultiselect
          disabled={disabled}
          selected={selected}
          onClose={onClose || (() => {})}
          onChange={onChange}
          listOptions={options}
          fontStyle={{ fontSize: 14, fontColor: ribbonStyle.color }}
          label={labelPlacement === 'placeholder' ? label : undefined}
          width={width}
          height={height}
        />
      }
      label={
        labelPlacement !== 'placeholder' ? (
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              fontSize: { xs: '13px', xl: '14px' },
              color: disabled ? ribbonStyle.disabledColor : ribbonStyle.color,
            }}>
            {label}
          </Typography>
        ) : undefined
      }
    />
  )
}

export const RibbonCheckbox = ({
  checked = false,
  indeterminate = false,
  onChange = () => {},
  label,
  labelPlacement = 'start',
  checkboxStyle = {},
  fontSize,
  size = 'small',
  disabled = false,
}) => {
  const { ribbonStyle } = useInnovaTheme()
  const [checkedState, setCheckedState] = useState(checked)

  useEffect(() => {
    setCheckedState(checked)
  }, [checked])

  return (
    <FormControlLabel
      control={
        <Checkbox
          disabled={disabled}
          indeterminate={indeterminate}
          checked={checkedState}
          style={{ color: disabled ? ribbonStyle.disabledColor : ribbonStyle.color, padding: 2, ...checkboxStyle }}
          size={size}
          onChange={(e) => {
            onChange(e.target.checked)
            setCheckedState(e.target.checked)
          }}
        />
      }
      label={
        <Typography
          sx={{
            fontSize: fontSize || { xs: '13px', xl: '14px' },
            color: disabled ? ribbonStyle.disabledColor : ribbonStyle.color,
            textWrap: 'nowrap',
          }}>
          {label}
        </Typography>
      }
      sx={{ paddingX: 1 }}
      labelPlacement={labelPlacement}
    />
  )
}

const pickerTextFieldSx = (c) => ({
  '& .MuiInputBase-input': { color: c, padding: 0.75 },
  '& .MuiSvgIcon-root': { color: c },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#000' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: c },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c },
  '& .MuiFormLabel-root': { color: c },
})

export const renderFloatingInput = (label, color) => (params) => {
  const id = params?.inputProps?.id || params?.id || `${label.replace(/\s+/g, '-')}-input`
  const hasValue = !!params?.inputProps?.value
  const shrink = params?.InputLabelProps?.shrink ?? hasValue

  return (
    <FormControl
      fullWidth
      size='small'
      variant='outlined'
      error={params.error}
      disabled={params.disabled}
      required={params.required}
      sx={pickerTextFieldSx(color)}>
      <InputLabel htmlFor={id} shrink={shrink} sx={{ fontSize: '13px' }}>
        {label}
      </InputLabel>

      <OutlinedInput
        id={id}
        inputRef={params.inputRef}
        inputProps={params.inputProps}
        endAdornment={params.InputProps?.endAdornment}
        label={label}
        notched={shrink}
      />
    </FormControl>
  )
}

export default Ribbon
